import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './contexts/authStore'
import { useAuthCheck } from './hooks/useAuthCheck'
import Layout from './components/common/Layout'
import AdminLayout from './components/admin/AdminLayout'
import CustomerLayout from './components/customer/CustomerLayout'

// Public Pages
import Home from './pages/public/Home'
import Apps from './pages/public/Apps'
import AppDetails from './pages/public/AppDetails'
import Checkout from './pages/public/Checkout'
import CheckoutSuccess from './pages/public/CheckoutSuccess'
import Login from './pages/public/Login'
import Contact from './pages/public/Contact'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/auth/ResetPassword'
import VerifyEmail from './pages/auth/VerifyEmail'
import ResendVerification from './pages/auth/ResendVerification'
import DynamicPage from './pages/public/DynamicPage'

// Customer Pages
import CustomerDashboard from './pages/customer/CustomerDashboard'
import CustomerAccountPage from './pages/customer/CustomerAccountPage'
import CustomerAppsPage from './pages/customer/CustomerAppsPage'
import CustomerInvoicesPage from './pages/customer/CustomerInvoicesPage'
import CustomerTicketsPage from './pages/customer/CustomerTicketsPage'
import CustomerSessionsPage from './pages/customer/CustomerSessionsPage'

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard'
import AccountPage from './pages/admin/AccountPage'
import ChangePasswordPage from './pages/admin/ChangePasswordPage'
import CustomersManagement from './pages/admin/CustomersManagement'
import AppsManagement from './pages/admin/AppsManagement'
import AppPlansManagement from './pages/admin/AppPlansManagement'
import AppSystemSettings from './pages/admin/AppSystemSettings'
import AppEditPage from './pages/admin/AppEditPage'
import SubscriptionsManagement from './pages/admin/SubscriptionsManagement'
import InvoiceManagement from './pages/admin/InvoiceManagement'
import PagesManagement from './pages/admin/PagesManagement'
import PageEditor from './pages/admin/PageEditor'
import SettingsManagement from './pages/admin/SettingsManagement'
import MenuManagement from './pages/admin/MenuManagement'
import CMSManagement from './pages/admin/CMSManagement'
import MessagesManagement from './pages/admin/MessagesManagement'
import SupportManagement from './pages/admin/SupportManagement'
import AdminSessionsPage from './pages/admin/AdminSessionsPage'
import ServicesManagement from './pages/admin/ServicesManagement'
import MigrationsManagement from './pages/admin/MigrationsManagement'

function ProtectedRoute({ children, allowedType }) {
  const { isAuthenticated, userType, isLoading, isRedirecting } = useAuthStore()

  if (isLoading || isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{isRedirecting ? 'Weiterleitung...' : 'Laden...'}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (allowedType && userType !== allowedType) {
    return <Navigate to="/" replace />
  }

  return children
}

function AdminProtectedRoute({ children }) {
  const { isAuthenticated, userType, isLoading, isRedirecting } = useAuthStore()

  if (isLoading || isRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{isRedirecting ? 'Weiterleitung...' : 'Laden...'}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (userType !== 'admin') {
    return <Navigate to="/" replace />
  }

  return <AdminLayout>{children}</AdminLayout>
}

function CustomerProtectedRoute({ children }) {
  const { isAuthenticated, userType, isLoading, isRedirecting } = useAuthStore()

  if (isLoading || isRedirecting) {
    return (
      <div className="wc-theme" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="wc-spinner" style={{ margin: '0 auto' }} />
          <p className="wc-muted" style={{ marginTop: 16, fontSize: 14 }}>{isRedirecting ? 'Weiterleitung…' : 'Laden…'}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (userType !== 'customer') {
    return <Navigate to="/" replace />
  }

  return <CustomerLayout>{children}</CustomerLayout>
}

function App() {
  // Check auth status on app load
  useAuthCheck()

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/apps" element={<Layout><Apps /></Layout>} />
      <Route path="/apps/:slug" element={<Layout><AppDetails /></Layout>} />
      <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
      <Route path="/checkout/success" element={<Layout><CheckoutSuccess /></Layout>} />
      <Route path="/login" element={<Layout><Login /></Layout>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/resend-verification" element={<ResendVerification />} />
      <Route path="/contact" element={<Layout><Contact /></Layout>} />

      {/* CMS Pages - dynamisch aus Datenbank */}
      <Route path="/impressum" element={<Layout><DynamicPage /></Layout>} />
      <Route path="/datenschutz" element={<Layout><DynamicPage /></Layout>} />
      <Route path="/agb" element={<Layout><DynamicPage /></Layout>} />

      {/* Customer Routes */}
      <Route path="/customer" element={<CustomerProtectedRoute><CustomerDashboard /></CustomerProtectedRoute>} />
      <Route path="/customer/account" element={<CustomerProtectedRoute><CustomerAccountPage /></CustomerProtectedRoute>} />
      <Route path="/customer/apps" element={<CustomerProtectedRoute><CustomerAppsPage /></CustomerProtectedRoute>} />
      <Route path="/customer/invoices" element={<CustomerProtectedRoute><CustomerInvoicesPage /></CustomerProtectedRoute>} />
      <Route path="/customer/support" element={<CustomerProtectedRoute><CustomerTicketsPage /></CustomerProtectedRoute>} />
      <Route path="/customer/sessions" element={<CustomerProtectedRoute><CustomerSessionsPage /></CustomerProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      <Route path="/admin/account" element={<AdminProtectedRoute><AccountPage /></AdminProtectedRoute>} />
      <Route path="/admin/change-password" element={<AdminProtectedRoute><ChangePasswordPage /></AdminProtectedRoute>} />
      <Route path="/admin/customers" element={<AdminProtectedRoute><CustomersManagement /></AdminProtectedRoute>} />
      <Route path="/admin/apps" element={<AdminProtectedRoute><AppsManagement /></AdminProtectedRoute>} />
      <Route path="/admin/apps/new" element={<AdminProtectedRoute><AppEditPage /></AdminProtectedRoute>} />
      <Route path="/admin/apps/:appId/edit" element={<AdminProtectedRoute><AppEditPage /></AdminProtectedRoute>} />
      <Route path="/admin/apps/:appId/plans" element={<AdminProtectedRoute><AppPlansManagement /></AdminProtectedRoute>} />
      <Route path="/admin/apps/:appId/system-settings" element={<AdminProtectedRoute><AppSystemSettings /></AdminProtectedRoute>} />
      <Route path="/admin/subscriptions" element={<AdminProtectedRoute><SubscriptionsManagement /></AdminProtectedRoute>} />
      <Route path="/admin/invoices" element={<AdminProtectedRoute><InvoiceManagement /></AdminProtectedRoute>} />
      <Route path="/admin/pages" element={<AdminProtectedRoute><PagesManagement /></AdminProtectedRoute>} />
      <Route path="/admin/pages/new" element={<AdminProtectedRoute><PageEditor /></AdminProtectedRoute>} />
      <Route path="/admin/pages/edit/:id" element={<AdminProtectedRoute><PageEditor /></AdminProtectedRoute>} />
      <Route path="/admin/settings" element={<AdminProtectedRoute><SettingsManagement /></AdminProtectedRoute>} />
      <Route path="/admin/menu" element={<AdminProtectedRoute><MenuManagement /></AdminProtectedRoute>} />
      <Route path="/admin/cms" element={<AdminProtectedRoute><CMSManagement /></AdminProtectedRoute>} />
      <Route path="/admin/messages" element={<AdminProtectedRoute><MessagesManagement /></AdminProtectedRoute>} />
      <Route path="/admin/support" element={<AdminProtectedRoute><SupportManagement /></AdminProtectedRoute>} />
      <Route path="/admin/sessions" element={<AdminProtectedRoute><AdminSessionsPage /></AdminProtectedRoute>} />
      <Route path="/admin/services" element={<AdminProtectedRoute><ServicesManagement /></AdminProtectedRoute>} />
      <Route path="/admin/migrations" element={<AdminProtectedRoute><MigrationsManagement /></AdminProtectedRoute>} />

      {/* Dynamic CMS Pages - Catch-all für weitere CMS-Seiten */}
      <Route path="/:slug" element={<Layout><DynamicPage /></Layout>} />

      {/* Final Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
